import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'prof_kylian_viewed_content';

export type ContentType = 'fiches' | 'videos' | 'quiz' | 'examens';

interface ViewedContent {
  [key: string]: {
    viewedAt: string;
    type: ContentType;
  };
}

export function useViewedContent() {
  const [viewedContent, setViewedContent] = useState<ViewedContent>({});
  const [isLoading, setIsLoading] = useState(true);

  // Charger les contenus vus au démarrage
  useEffect(() => {
    loadViewedContent();
  }, []);

  const loadViewedContent = async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        setViewedContent(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Error loading viewed content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Marquer un contenu comme vu
  const markAsViewed = useCallback(async (contentId: string, type: ContentType) => {
    try {
      const newViewedContent = {
        ...viewedContent,
        [contentId]: {
          viewedAt: new Date().toISOString(),
          type,
        },
      };
      
      setViewedContent(newViewedContent);
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newViewedContent));
    } catch (error) {
      console.error('Error marking content as viewed:', error);
    }
  }, [viewedContent]);

  // Vérifier si un contenu a été vu
  const isViewed = useCallback((contentId: string): boolean => {
    return !!viewedContent[contentId];
  }, [viewedContent]);

  // Obtenir la date de visionnage
  const getViewedDate = useCallback((contentId: string): string | null => {
    return viewedContent[contentId]?.viewedAt || null;
  }, [viewedContent]);

  // Compter les contenus vus par type
  const getViewedCount = useCallback((type: ContentType): number => {
    return Object.values(viewedContent).filter(v => v.type === type).length;
  }, [viewedContent]);

  // Réinitialiser tous les contenus vus (optionnel)
  const resetAllViewed = useCallback(async () => {
    try {
      setViewedContent({});
      await AsyncStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error resetting viewed content:', error);
    }
  }, []);

  return {
    isLoading,
    viewedContent,
    markAsViewed,
    isViewed,
    getViewedDate,
    getViewedCount,
    resetAllViewed,
  };
}
